/*
 * Trello-Export (Board-Menü → Drucken, Exportieren und Teilen → Als JSON exportieren) lesen.
 * Ohne zod, damit auch der Browser die Vorschau berechnen kann. Alles ist defensiv gelesen –
 * Exporte unterscheiden sich je nach Alter und Power-Ups.
 */
import type { LabelColor } from './types.js';

type Obj = Record<string, unknown>;
const isObj = (x: unknown): x is Obj => !!x && typeof x === 'object' && !Array.isArray(x);
const arr = (x: unknown): Obj[] => (Array.isArray(x) ? x.filter(isObj) : []);
const str = (x: unknown): string => (typeof x === 'string' ? x : '');
const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
const isoOrNull = (x: unknown): string | null => {
  if (typeof x !== 'string' || !x) return null;
  const d = new Date(x);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
const byPos = (a: Obj, b: Obj) => num(a.pos) - num(b.pos);

/** Trello-IDs sind MongoDB-ObjectIds: die ersten 8 Hex-Zeichen sind der Anlagezeitpunkt */
export function trelloCreatedAt(id: string): string | null {
  if (!/^[0-9a-f]{24}$/i.test(id)) return null;
  return new Date(parseInt(id.slice(0, 8), 16) * 1000).toISOString();
}

const COLOR: Record<string, LabelColor> = {
  green: 'green',
  yellow: 'yellow',
  orange: 'orange',
  red: 'red',
  purple: 'purple',
  blue: 'blue',
  sky: 'sky',
  lime: 'lime',
  pink: 'pink',
  black: 'gray',
};
export function trelloColor(c: unknown): LabelColor {
  const base = str(c).replace(/_(dark|light)$/, '');
  return COLOR[base] ?? 'gray';
}

export function isTrelloExport(data: unknown): data is Obj {
  return isObj(data) && Array.isArray(data.lists) && Array.isArray(data.cards) && typeof data.name === 'string';
}

export interface TrelloMember {
  key: string;
  name: string;
  username: string;
}

export interface TrelloSummary {
  boardName: string;
  lists: number;
  closedLists: number;
  cards: number;
  archivedCards: number;
  labels: number;
  checklistItems: number;
  comments: number;
  attachments: number;
  customFields: number;
  members: TrelloMember[];
}

export interface ImportCard {
  key: string;
  columnKey: string;
  title: string;
  /** Markdown aus Trello */
  description: string;
  attachments: { name: string; url: string }[];
  labelKeys: string[];
  memberKeys: string[];
  dueAt: string | null;
  startAt: string | null;
  /** Termine ohne Uhrzeit (Text- und CSV-Import) */
  allDay?: boolean;
  completed: boolean;
  archived: boolean;
  createdAt: string | null;
  checklist: { text: string; done: boolean }[];
  comments: { authorKey: string | null; authorName: string; text: string; at: string | null }[];
  /** 0–4 (Tabellen-Import, z. B. Planner) */
  priority?: number;
  /** 0.71: wartet auf diese Karten (Schlüssel im Plan; Asana/Jira) */
  dependsOn?: string[];
}

export interface ImportPlan {
  title: string;
  description: string;
  columns: { key: string; title: string }[];
  labels: { key: string; name: string; color: LabelColor }[];
  members: TrelloMember[];
  cards: ImportCard[];
}

function members(d: Obj): TrelloMember[] {
  return arr(d.members).map((m) => ({ key: str(m.id), name: str(m.fullName) || str(m.username) || 'Unbekannt', username: str(m.username) }));
}

function commentActions(d: Obj): Obj[] {
  return arr(d.actions).filter((a) => a.type === 'commentCard' && isObj(a.data) && isObj(a.data.card));
}

export function summarizeTrello(d: Obj): TrelloSummary {
  const lists = arr(d.lists);
  const open = new Set(lists.filter((l) => !l.closed).map((l) => str(l.id)));
  const cards = arr(d.cards).filter((c) => open.has(str(c.idList)));
  return {
    boardName: str(d.name) || 'Trello-Board',
    lists: open.size,
    closedLists: lists.length - open.size,
    cards: cards.filter((c) => !c.closed).length,
    archivedCards: cards.filter((c) => c.closed).length,
    labels: arr(d.labels).length,
    checklistItems: arr(d.checklists).reduce((n, c) => n + arr(c.checkItems).length, 0),
    comments: commentActions(d).length,
    attachments: cards.reduce((n, c) => n + arr(c.attachments).length, 0),
    customFields: arr(d.customFields).length,
    members: members(d),
  };
}

export function planTrelloImport(d: Obj, opts: { includeArchived: boolean; includeComments: boolean }): ImportPlan {
  const lists = arr(d.lists)
    .filter((l) => !l.closed)
    .sort(byPos);
  const listIds = new Set(lists.map((l) => str(l.id)));
  const checklists = arr(d.checklists);
  const comments = opts.includeComments ? commentActions(d) : [];
  const memberNames = new Map(members(d).map((m) => [m.key, m.name]));

  const cards: ImportCard[] = arr(d.cards)
    .filter((c) => listIds.has(str(c.idList)) && (opts.includeArchived || !c.closed))
    .sort(byPos)
    .map((c) => {
      const id = str(c.id);
      const lists = checklists.filter((cl) => str(cl.idCard) === id).sort(byPos);
      const checklist = lists.flatMap((cl) =>
        arr(cl.checkItems)
          .sort(byPos)
          .map((it) => ({
            // Mehrere Checklisten: Name voranstellen, damit die Gruppierung lesbar bleibt
            text: (lists.length > 1 ? `${str(cl.name)}: ` : '') + str(it.name),
            done: it.state === 'complete',
          })),
      );
      const cardComments = comments
        .filter((a) => str((a.data as Obj & { card: Obj }).card.id) === id)
        .map((a) => {
          const creator = isObj(a.memberCreator) ? a.memberCreator : {};
          const authorKey = str(a.idMemberCreator) || str(creator.id) || null;
          return {
            authorKey,
            authorName: str(creator.fullName) || (authorKey ? memberNames.get(authorKey) : '') || 'Unbekannt',
            text: str((a.data as Obj).text),
            at: isoOrNull(a.date),
          };
        })
        .filter((x) => x.text.trim())
        .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
      return {
        key: id,
        columnKey: str(c.idList),
        title: str(c.name).trim().slice(0, 500) || 'Ohne Titel',
        description: str(c.desc),
        attachments: arr(c.attachments)
          .map((a) => ({ name: str(a.name) || str(a.url), url: str(a.url) }))
          .filter((a) => /^https?:\/\//.test(a.url)),
        labelKeys: Array.isArray(c.idLabels) ? c.idLabels.filter((x): x is string => typeof x === 'string') : [],
        memberKeys: Array.isArray(c.idMembers) ? c.idMembers.filter((x): x is string => typeof x === 'string') : [],
        dueAt: isoOrNull(c.due),
        startAt: isoOrNull(c.start),
        completed: c.dueComplete === true,
        archived: c.closed === true,
        createdAt: trelloCreatedAt(id),
        checklist: checklist.filter((x) => x.text.trim()),
        comments: cardComments,
      };
    });

  return {
    title: (str(d.name) || 'Trello-Board').slice(0, 200),
    description: str(d.desc),
    columns: lists.map((l) => ({ key: str(l.id), title: str(l.name).slice(0, 200) || 'Liste' })),
    labels: arr(d.labels).map((l) => ({ key: str(l.id), name: str(l.name).slice(0, 40), color: trelloColor(l.color) })),
    members: members(d),
    cards,
  };
}
